/**
 * One list contract for the whole UI.
 *
 * ## The measured defect
 *
 * The gateway returns list responses in TWO shapes and nothing says which:
 *
 *   - `ProvenanceEnvelopeMiddleware` stamps `__source` / `__freshAt` onto
 *     top-level JSON **objects** only — deliberately not arrays, so array-shaped
 *     endpoints stay arrays.
 *   - Endpoints that call `WithProvenance.Of(...)` pre-wrap themselves into
 *     `{ __source, __freshAt, data: [...] }`.
 *
 * Verified live against demo.tyndo.io: `/api/v1/multisite/sites` returns
 * `{…, "data": []}` while `/api/v1/alarms/active` returns `[]`. Views could not
 * tell, and the ones that guessed wrong died with
 * `TypeError: … .map is not a function`, taking the whole pane white.
 *
 * ## Why `?? []` did not save the guarded call sites
 *
 * The audit counted 570 unguarded `.map(` against 125 using `(x ?? []).map(`.
 * That second number is NOT a safety margin: `??` guards *null and undefined*,
 * and an envelope is neither. `({data: []} ?? []).map` still throws. Both
 * populations were exposed; the guard only ever protected against a missing
 * response, never against the wrong shape.
 *
 * ## The fix
 *
 * `normalizeListEnvelope` runs in the API layer (see `useApi.ts`), so a view
 * NEVER sees an envelope and no call site has to branch on shape. Provenance is
 * not lost: `__source` / `__freshAt` are re-attached to the returned array as
 * NON-ENUMERABLE properties, so
 *
 *   - `Array.isArray(result)` is true and `.map` works,
 *   - `result.__source` still reads for `ProvenanceBadge`,
 *   - `JSON.stringify` / spread / `Object.keys` see a plain array.
 *
 * `unwrapList` is the same collapse exposed for defensive use at a boundary the
 * API layer does not own (a websocket frame, a hand-rolled `fetch`, a test
 * fixture).
 */

/** The provenance fields the gateway stamps onto an envelope. */
export interface ListProvenance {
  __source?: string | null;
  __freshAt?: string | null;
}

/** A list response that arrived wrapped. */
export interface ProvenanceListEnvelope<T> extends ListProvenance {
  data?: T[];
  items?: T[];
}

/** A list response as it may arrive on the wire. */
export type MaybeEnvelopedList<T> = T[] | ProvenanceListEnvelope<T> | null | undefined;

const PROVENANCE_KEYS = ['__source', '__freshAt'] as const;

/**
 * Collapse a bare-array-or-envelope list response into a plain array.
 *
 * Returns `[]` for null/undefined and for an object that is neither shape, so a
 * caller can always `.map` the result. Never throws.
 */
export function unwrapList<T>(value: MaybeEnvelopedList<T>): T[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== 'object') return [];
  if (Array.isArray(value.data)) return value.data;
  if (Array.isArray(value.items)) return value.items;
  return [];
}

/**
 * True when `value` is a provenance envelope wrapping a list — an object (not an
 * array) whose `data` or `items` is an array.
 *
 * Deliberately does NOT require `__source` to be present. An endpoint may wrap
 * without provenance, and the shape is the thing views break on.
 */
export function isListEnvelope<T>(value: unknown): value is ProvenanceListEnvelope<T> {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return false;
  const o = value as ProvenanceListEnvelope<T>;
  return Array.isArray(o.data) || Array.isArray(o.items);
}

/**
 * The API-layer normalizer. If `body` is a list envelope, return its array with
 * provenance re-attached non-enumerably. Anything else passes through UNTOUCHED
 * — a single-object response with a `data` property that is not an array, a
 * paginated `PageEnvelope` (which carries `total`/`page` and is a real contract
 * views read), a scalar, or `undefined` from a 204.
 */
export function normalizeListEnvelope<T>(body: T): T {
  if (!isListEnvelope(body)) return body;

  const envelope = body as ProvenanceListEnvelope<unknown>;

  // A paginated envelope is a REAL contract — views read `total`/`page`/`cursor`
  // off it to render pagers. Collapsing it to its rows would silently break
  // every pager in the app, so it is left alone; those views already index
  // `.data` explicitly.
  const asRecord = envelope as Record<string, unknown>;
  for (const paged of ['total', 'totalCount', 'page', 'pageSize', 'cursor', 'nextCursor']) {
    if (paged in asRecord) return body;
  }

  const rows = (Array.isArray(envelope.data) ? envelope.data : envelope.items) as unknown[];

  // Non-enumerable so the array stringifies, spreads and Object.keys()s exactly
  // like the bare-array responses it now matches — while ProvenanceBadge's
  // `response.__source` read keeps working.
  for (const key of PROVENANCE_KEYS) {
    if (key in asRecord) {
      Object.defineProperty(rows, key, {
        value: asRecord[key],
        enumerable: false,
        configurable: true,
        writable: true,
      });
    }
  }

  return rows as unknown as T;
}
